/**
 * /admin/usage — platform-wide rollup of the Usage data.
 *
 * Same Cloudflare Analytics Engine source as the per-app Usage tab, just
 * without the app filter and with a per-app breakdown joined to the apps
 * table for slug + name. Hard-deleted apps still appear (since their
 * audit history matters); they render with the raw id instead of a link.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api/client.js';
import type { GlobalUsageResponse, UsageRange } from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconBolt, IconInfo, IconRocket, IconUsers } from '../../components/Icons.js';
import { UsageChart } from '../../components/UsageChart.js';
import {
  Banner,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  PageSkeleton,
} from '../../components/ui.js';

interface State {
  loading: boolean;
  data?: GlobalUsageResponse;
  error?: string;
}

const RANGE_OPTIONS: { value: UsageRange; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: '90d', label: '90 days' },
];

export function AdminUsage() {
  const [range, setRange] = useState<UsageRange>('week');
  const [state, setState] = useState<State>({ loading: true });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const data = await api.get<GlobalUsageResponse>(`/api/admin/usage?range=${range}`);
      setState({ loading: false, data });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : String(err),
      });
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.loading && !state.data) return <PageSkeleton rows={4} />;
  if (state.error) return <Banner tone="error">{state.error}</Banner>;
  const data = state.data;
  if (!data) return null;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Usage"
        description="Platform-wide rollup. Each event is one authenticated request through the Kin SDK."
      />

      <AdminTabs />

      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div
          role="tablist"
          aria-label="Time range"
          className="inline-flex items-center gap-1 p-1 rounded-lg bg-surface-2 border border-line"
        >
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              role="tab"
              aria-selected={range === opt.value}
              type="button"
              onClick={() => setRange(opt.value)}
              className={
                'px-3 py-1 rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
                (range === opt.value
                  ? 'bg-surface text-ink shadow-card'
                  : 'text-muted hover:text-ink')
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-faint inline-flex items-center gap-1.5">
          <IconInfo size={12} />
          Updates roughly once a minute.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
        <KpiCard
          label="Authenticated requests"
          value={data.totals.events.toLocaleString()}
          hint="in the selected range"
          icon={<IconBolt size={12} />}
        />
        <KpiCard
          label="Unique users"
          value={data.totals.uniqueUsers.toLocaleString()}
          hint="distinct people across all apps"
          icon={<IconUsers size={12} />}
        />
        <KpiCard
          label="Active apps"
          value={data.totals.activeApps.toLocaleString()}
          hint="apps with at least one event"
          icon={<IconRocket size={12} />}
        />
      </div>

      <Card className="mb-5">
        <CardHeader
          title="Platform activity over time"
          description={
            range === 'day' ? 'Hourly buckets, last 24 hours' : 'Daily buckets across the range'
          }
        />
        <div className="p-5 pt-3">
          <UsageChart series={data.series} range={range} height={220} />
        </div>
      </Card>

      <Card>
        <CardHeader
          title="By app"
          description={`${data.perApp.length} app${data.perApp.length === 1 ? '' : 's'} with activity`}
        />
        {data.perApp.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState
              icon={<IconRocket size={18} />}
              title="No activity yet"
              description="Once apps start receiving authenticated requests they'll show up here."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted font-medium">
                  <th className="text-left font-medium px-5 py-2.5">App</th>
                  <th className="text-right font-medium px-3 py-2.5 tabular">Requests</th>
                  <th className="text-right font-medium px-5 py-2.5 tabular">Unique users</th>
                </tr>
              </thead>
              <tbody>
                {data.perApp.map((row) => (
                  <tr key={row.app_id} className="border-t border-line">
                    <td className="px-5 py-2.5 min-w-0">
                      {row.slug ? (
                        <Link
                          to={`/admin/apps/${row.slug}`}
                          className="font-medium text-ink hover:text-brand transition-colors"
                        >
                          {row.name ?? row.slug}
                        </Link>
                      ) : (
                        <span className="text-faint italic">deleted app · {row.app_id}</span>
                      )}
                      {row.slug && row.name && (
                        <div className="text-xs text-muted font-mono">{row.slug}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular font-medium text-ink">
                      {row.events.toLocaleString()}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular text-ink">
                      {row.uniqueUsers.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <div className="p-5">
        <div className="text-xs uppercase tracking-wider text-muted font-medium inline-flex items-center gap-1.5">
          {icon} {label}
        </div>
        <div className="font-display text-4xl font-semibold tabular text-ink mt-2">{value}</div>
        <div className="text-xs text-muted mt-1">{hint}</div>
      </div>
    </Card>
  );
}
